import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Inbox, RefreshCw, Plus, X, Mail, Baby, CalendarDays, Users,
  TrendingUp, AlertTriangle, CheckCircle2, ArrowRight, Trash2, Sparkles,
  ChevronRight, Phone, Clock, Send, FileText, Copy, Check, Pencil, Search,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { demandesApi, modelesApi } from '../../utils/api';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';

const JOURS_LABEL = { lundi: 'Lundi', mardi: 'Mardi', mercredi: 'Mercredi', jeudi: 'Jeudi', vendredi: 'Vendredi' };
const JOURS_COURT = { lundi: 'Lun', mardi: 'Mar', mercredi: 'Mer', jeudi: 'Jeu', vendredi: 'Ven' };

const STATUT_MAP = {
  nouvelle: { label: 'Nouvelle', pill: 'bg-gold-100 text-gold-700' },
  traitee: { label: 'Traitée', pill: 'bg-emerald-50 text-emerald-600' },
  convertie: { label: 'Convertie', pill: 'bg-navy-100 text-navy-700' },
  rejetee: { label: 'Rejetée', pill: 'bg-navy-50 text-navy-300' },
};

const FILTRES = [
  { key: 'all', label: 'Toutes' },
  { key: 'nouvelle', label: 'Nouvelles' },
  { key: 'traitee', label: 'Traitées' },
  { key: 'rejetee', label: 'Rejetées' },
];

function formatDate(d) {
  if (!d) return '—';
  try { return format(parseISO(d), 'd MMM yyyy', { locale: fr }); } catch { return d; }
}
function relDate(d) {
  if (!d) return '';
  try { return format(parseISO(d.replace(' ', 'T')), 'd MMM', { locale: fr }); } catch { return ''; }
}
// Date + heure précises (« 18 sept. à 14h30 ») pour le suivi des réponses
function dateHeure(d) {
  if (!d) return '';
  try { return format(parseISO(d.replace(' ', 'T')), "d MMM 'à' HH'h'mm", { locale: fr }); } catch { return ''; }
}
function initials(d) {
  const a = (d.enfant_prenom || '').trim()[0] || '';
  const b = (d.enfant_nom || '').trim()[0] || '';
  return (a + b).toUpperCase() || '?';
}
function hexA(hex, a) {
  if (!hex || hex[0] !== '#') return `rgba(192,152,90,${a})`;
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
}
function margeTone(marge) {
  if (!marge) return { pill: 'bg-navy-50 text-navy-300', text: 'text-navy-300' };
  const p = marge.marge_pct;
  if (p >= 50) return { pill: 'bg-emerald-50 text-emerald-600', text: 'text-emerald-600' };
  if (p >= 30) return { pill: 'bg-gold-100 text-gold-700', text: 'text-gold-700' };
  return { pill: 'bg-red-50 text-red-500', text: 'text-red-500' };
}

/* ---------- Jauge de remplissage hebdomadaire ---------- */
function FillBar({ occupation }) {
  const avant = occupation.taux_remplissage_avant || 0;
  const apres = occupation.taux_remplissage_apres || 0;
  const over = occupation.depassement_hebdo;
  const wAvant = Math.min(avant, 100);
  const wAjout = Math.min(Math.max(apres - avant, 0), 100 - wAvant);
  const ajoutColor = over ? 'bg-red-400' : apres >= 90 ? 'bg-gold-500' : 'bg-gold-400';
  return (
    <div>
      <div className="flex items-baseline justify-between mb-1.5">
        <span className="text-[13px] text-navy-400">Remplissage hebdomadaire</span>
        <span className="text-[13px]">
          <span className="text-navy-300">{avant}%</span>
          <span className="text-navy-200 mx-1.5">→</span>
          <span className={`font-semibold ${over ? 'text-red-500' : apres >= 90 ? 'text-gold-700' : 'text-navy-800'}`}>{apres}%</span>
        </span>
      </div>
      <div className="relative h-2.5 rounded-full bg-navy-100 overflow-hidden">
        <div className="absolute inset-y-0 left-0 bg-navy-300" style={{ width: `${wAvant}%` }} />
        <div className={`absolute inset-y-0 ${ajoutColor} transition-all duration-500`} style={{ left: `${wAvant}%`, width: `${wAjout}%` }} />
        <div className="absolute inset-y-0 right-0 w-px bg-navy-400/30" />
      </div>
      <div className="mt-1 text-[11px] text-navy-300">
        Capacité : {occupation.capacite} places · max {occupation.capacite_max_jour}/jour (surnombre toléré)
      </div>
    </div>
  );
}

function DayChip({ j, occupation }) {
  const danger = !j.rentre;
  const warn = j.surnombre && j.rentre;
  const tone = danger ? 'border-red-200 bg-red-50/60' : warn ? 'border-gold-200 bg-gold-50' : 'border-cream-300/70 bg-cream-50';
  const num = danger ? 'text-red-500' : warn ? 'text-gold-700' : 'text-navy-800';
  return (
    <div className={`rounded-2xl border ${tone} px-4 py-3`}>
      <div className="flex items-center justify-between">
        <span className="font-semibold text-[13px] text-navy-600">{JOURS_LABEL[j.jour]}</span>
        <span className="text-[11px] text-navy-300 tabular-nums">{j.demande.debut}–{j.demande.fin}</span>
      </div>
      <div className="mt-1.5 flex items-end gap-1.5">
        <span className={`text-[26px] leading-none font-semibold tracking-apple ${num}`}>{j.pic_avec_demande}</span>
        <span className="text-[12px] text-navy-300 mb-0.5">/ {occupation.capacite_max_jour}</span>
      </div>
      <div className="mt-1 text-[11px] text-navy-400">
        {j.rentre ? `${Math.max(0, occupation.capacite_max_jour - j.pic_avec_demande)} place(s) libre(s)` : `plafond ${occupation.capacite_max_jour} dépassé`}
      </div>
      {warn && <div className="mt-1 text-[11px] text-gold-700">Surnombre · à compenser</div>}
      {j.pro_supplementaire && (
        <div className="mt-1.5 inline-flex items-center gap-1 text-[11px] text-gold-700 bg-gold-100 rounded-full px-2 py-0.5">
          <Users size={11} /> +1 pro
        </div>
      )}
    </div>
  );
}

/* ---------- Modale de saisie manuelle ---------- */
function ManuelModal({ onClose, onCreated }) {
  const { structures } = useApp();
  const [form, setForm] = useState({ subject: '', from: '', text: '', structure_id: '' });
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.text.trim()) return;
    setSaving(true);
    try {
      const { data } = await demandesApi.creerManuel(form);
      onCreated(data);
      onClose();
    } catch (err) {
      alert('Erreur : ' + (err.response?.data?.error || err.message));
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-navy-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-backdrop" onClick={onClose}>
      <div className="bg-white rounded-[22px] shadow-2xl w-full max-w-xl animate-sheet overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 pt-5 pb-3">
          <div>
            <h3 className="font-serif text-[21px] text-navy-900">Nouvelle demande</h3>
            <p className="text-[13px] text-navy-300">Collez le mail, Georges l'analyse</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-navy-50 hover:bg-navy-100 flex items-center justify-center text-navy-400 transition-colors">
            <X size={17} />
          </button>
        </div>
        <div className="px-6 pb-6 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <input className="form-input rounded-xl" placeholder="Objet (optionnel)" value={form.subject} onChange={e => upd('subject', e.target.value)} />
            <input className="form-input rounded-xl" placeholder="Expéditeur (optionnel)" value={form.from} onChange={e => upd('from', e.target.value)} />
          </div>
          <select className="form-input rounded-xl" value={form.structure_id} onChange={e => upd('structure_id', e.target.value)}>
            <option value="">Structure : détection automatique</option>
            {structures.map(s => <option key={s.id} value={s.id}>{s.nom}</option>)}
          </select>
          <textarea className="form-input rounded-xl h-44 resize-none" placeholder="Collez ici le contenu du mail de demande…" value={form.text} onChange={e => upd('text', e.target.value)} />
          <div className="flex justify-end gap-2 pt-1">
            <button onClick={onClose} className="apple-btn-secondary">Annuler</button>
            <button onClick={submit} disabled={saving || !form.text.trim()} className="apple-btn-gold">
              <Sparkles size={15} /> {saving ? 'Analyse…' : 'Analyser'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- Modale : répondre en choisissant un mail type ---------- */
function ReponseModal({ demande, onClose, onSent }) {
  const [data, setData] = useState(null);
  const [selId, setSelId] = useState(null);
  const [destinataire, setDestinataire] = useState('');
  const [sujet, setSujet] = useState('');
  const [corps, setCorps] = useState('');
  const [sending, setSending] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(null);
  const [openRep, setOpenRep] = useState(null); // id de la réponse dont on affiche le corps

  useEffect(() => {
    let alive = true;
    demandesApi.getReponse(demande.id).then(({ data }) => {
      if (!alive) return;
      setData(data);
      setDestinataire(data.destinataire || '');
      if (data.modeles?.length) {
        const m = data.modeles[0];
        setSelId(m.id); setSujet(m.sujet); setCorps(m.corps);
      }
    }).catch(err => setError(err.response?.data?.error || err.message));
    return () => { alive = false; };
  }, [demande.id]);

  const choisir = (id) => {
    const m = data?.modeles.find(x => x.id === Number(id));
    if (!m) return;
    setSelId(m.id); setSujet(m.sujet); setCorps(m.corps);
  };

  const copier = () => {
    navigator.clipboard?.writeText(`${sujet ? sujet + '\n\n' : ''}${corps}`);
    setCopied(true); setTimeout(() => setCopied(false), 1800);
  };

  const envoyer = async () => {
    if (!destinataire.trim() || !corps.trim()) return;
    setSending(true); setError(null);
    try {
      const modele = data?.modeles.find(m => m.id === selId);
      await demandesApi.repondre(demande.id, {
        modele_id: selId, modele_nom: modele?.nom, destinataire, sujet, corps,
      });
      onSent();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
    setSending(false);
  };

  return (
    <div className="fixed inset-0 bg-navy-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-backdrop" onClick={onClose}>
      <div className="bg-white rounded-[22px] shadow-2xl w-full max-w-2xl animate-sheet overflow-hidden max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 pt-5 pb-3 flex-shrink-0">
          <div>
            <h3 className="font-serif text-[21px] text-navy-900">Répondre à la famille</h3>
            <p className="text-[13px] text-navy-300">
              {demande.enfant_prenom || 'Enfant'} {demande.enfant_nom || ''} · choisissez un mail type
            </p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-navy-50 hover:bg-navy-100 flex items-center justify-center text-navy-400 transition-colors">
            <X size={17} />
          </button>
        </div>

        <div className="px-6 pb-6 space-y-3 overflow-y-auto">
          {!data ? (
            <div className="text-center text-navy-300 py-10">Chargement…</div>
          ) : (
            <>
              {data.smtp_pret === false && (
                <div className="px-3 py-2 rounded-xl text-[12.5px] bg-gold-50 text-gold-700 flex items-center gap-2">
                  <AlertTriangle size={14} /> Envoi SMTP non configuré (.env) — vous pouvez copier le texte et l'envoyer depuis votre messagerie.
                </div>
              )}

              <div>
                <label className="text-[12px] font-semibold text-navy-500">Mail type</label>
                <select className="form-input rounded-xl mt-1" value={selId || ''} onChange={e => choisir(e.target.value)}>
                  {data.modeles.length === 0 && <option value="">Aucun modèle</option>}
                  {data.modeles.map(m => <option key={m.id} value={m.id}>{m.nom}</option>)}
                </select>
              </div>

              <div className="grid grid-cols-1 gap-3">
                <div>
                  <label className="text-[12px] font-semibold text-navy-500">Destinataire</label>
                  <input className="form-input rounded-xl mt-1" placeholder="email@exemple.fr" value={destinataire} onChange={e => setDestinataire(e.target.value)} />
                </div>
                <div>
                  <label className="text-[12px] font-semibold text-navy-500">Objet</label>
                  <input className="form-input rounded-xl mt-1" value={sujet} onChange={e => setSujet(e.target.value)} />
                </div>
                <div>
                  <label className="text-[12px] font-semibold text-navy-500">Message</label>
                  <textarea className="form-input rounded-xl mt-1 h-56 resize-none leading-relaxed" value={corps} onChange={e => setCorps(e.target.value)} />
                </div>
              </div>

              {data.reponses?.length > 0 && (
                <div className="rounded-xl bg-cream-100 border border-cream-300/60 p-3">
                  <div className="text-[11px] uppercase tracking-wide text-gold-600 font-semibold mb-1.5 flex items-center gap-1.5">
                    <Mail size={12} /> Suivi des réponses ({data.reponses.length})
                  </div>
                  <ul className="space-y-1.5">
                    {data.reponses.map(r => {
                      const open = openRep === r.id;
                      return (
                        <li key={r.id} className="text-[12.5px] text-navy-600">
                          <button type="button" onClick={() => setOpenRep(open ? null : r.id)}
                            className="w-full text-left flex items-start gap-2 hover:text-navy-900">
                            <ChevronRight size={13} className={`mt-0.5 flex-shrink-0 text-navy-300 transition-transform ${open ? 'rotate-90' : ''}`} />
                            <span>
                              <strong className="text-navy-800">{r.auteur_nom || 'Réponse envoyée'}</strong>
                              {' '}a répondu le <strong>{dateHeure(r.created_at)}</strong>
                              {r.modele_nom ? <span className="text-navy-400"> · modèle « {r.modele_nom} »</span> : null}
                              {r.destinataire ? <span className="text-navy-400"> · à {r.destinataire}</span> : null}
                            </span>
                          </button>
                          {open && (
                            <div className="ml-5 mt-1.5 rounded-lg bg-white border border-cream-300/60 p-3 space-y-1.5">
                              {r.sujet ? <div className="text-[12px]"><span className="text-navy-400">Objet : </span><span className="font-medium text-navy-800">{r.sujet}</span></div> : null}
                              <div className="text-[12.5px] text-navy-700 whitespace-pre-wrap leading-relaxed max-h-64 overflow-auto">{r.corps || <span className="italic text-navy-300">(corps non enregistré)</span>}</div>
                              {r.corps ? (
                                <button type="button"
                                  onClick={() => { navigator.clipboard?.writeText(`${r.sujet ? r.sujet + '\n\n' : ''}${r.corps}`); }}
                                  className="text-[11.5px] text-gold-600 hover:text-gold-700 font-medium">Copier ce message</button>
                              ) : null}
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              {error && (
                <div className="px-3 py-2 rounded-xl text-[12.5px] bg-red-50 text-red-600 flex items-center gap-2">
                  <AlertTriangle size={14} /> {error}
                </div>
              )}

              <div className="flex items-center gap-2 pt-1">
                <button onClick={copier} className="apple-btn-secondary">
                  {copied ? <><Check size={15} /> Copié</> : <><Copy size={15} /> Copier</>}
                </button>
                <button onClick={onClose} className="apple-btn-ghost ml-auto">Annuler</button>
                <button onClick={envoyer} disabled={sending || !destinataire.trim() || !corps.trim()} className="apple-btn-gold">
                  <Send size={15} /> {sending ? 'Envoi…' : 'Envoyer'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------- Modale : gérer les mails types ---------- */
function ModelesManager({ onClose }) {
  const [modeles, setModeles] = useState([]);
  const [variables, setVariables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // objet en cours d'édition (ou null)

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await modelesApi.getAll();
      setModeles(data.modeles); setVariables(data.variables || []);
    } catch { /* noop */ }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const nouveau = () => setEditing({ nom: '', sujet: '', corps: '' });

  const enregistrer = async () => {
    if (!editing.nom.trim() || !editing.sujet.trim() || !editing.corps.trim()) return;
    try {
      if (editing.id) await modelesApi.update(editing.id, editing);
      else await modelesApi.create(editing);
      setEditing(null); load();
    } catch (err) { alert('Erreur : ' + (err.response?.data?.error || err.message)); }
  };

  const supprimer = async (id) => {
    if (!window.confirm('Supprimer ce mail type ?')) return;
    try { await modelesApi.delete(id); load(); }
    catch (err) { alert('Erreur : ' + (err.response?.data?.error || err.message)); }
  };

  return (
    <div className="fixed inset-0 bg-navy-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-backdrop" onClick={onClose}>
      <div className="bg-white rounded-[22px] shadow-2xl w-full max-w-2xl animate-sheet overflow-hidden max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 pt-5 pb-3 flex-shrink-0">
          <div>
            <h3 className="font-serif text-[21px] text-navy-900">Mails types</h3>
            <p className="text-[13px] text-navy-300">Modèles proposés au moment de répondre</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-navy-50 hover:bg-navy-100 flex items-center justify-center text-navy-400 transition-colors">
            <X size={17} />
          </button>
        </div>

        <div className="px-6 pb-6 overflow-y-auto">
          {editing ? (
            <div className="space-y-3">
              <div>
                <label className="text-[12px] font-semibold text-navy-500">Nom du modèle</label>
                <input className="form-input rounded-xl mt-1" placeholder="Ex. Liste d'attente" value={editing.nom} onChange={e => setEditing({ ...editing, nom: e.target.value })} />
              </div>
              <div>
                <label className="text-[12px] font-semibold text-navy-500">Objet</label>
                <input className="form-input rounded-xl mt-1" value={editing.sujet} onChange={e => setEditing({ ...editing, sujet: e.target.value })} />
              </div>
              <div>
                <label className="text-[12px] font-semibold text-navy-500">Message</label>
                <textarea className="form-input rounded-xl mt-1 h-52 resize-none leading-relaxed" value={editing.corps} onChange={e => setEditing({ ...editing, corps: e.target.value })} />
              </div>
              {variables.length > 0 && (
                <div className="text-[11.5px] text-navy-400 bg-cream-100 border border-cream-300/60 rounded-xl p-3">
                  <span className="font-semibold text-navy-500">Variables :</span>{' '}
                  {variables.map(v => <code key={v.cle} className="mx-0.5 px-1 py-0.5 bg-white rounded text-gold-700">{v.cle}</code>)}
                  <div className="mt-1 text-navy-300">Elles sont remplacées automatiquement par les infos de chaque demande.</div>
                </div>
              )}
              <div className="flex justify-end gap-2 pt-1">
                <button onClick={() => setEditing(null)} className="apple-btn-ghost">Annuler</button>
                <button onClick={enregistrer} className="apple-btn-gold"><Check size={15} /> Enregistrer</button>
              </div>
            </div>
          ) : loading ? (
            <div className="text-center text-navy-300 py-10">Chargement…</div>
          ) : (
            <div className="space-y-2">
              {modeles.map(m => (
                <div key={m.id} className="flex items-center gap-3 px-4 py-3 rounded-xl border border-cream-300/60 bg-cream-50">
                  <FileText size={16} className="text-gold-500 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-[14px] text-navy-800 truncate">{m.nom}</div>
                    <div className="text-[12px] text-navy-300 truncate">{m.sujet}</div>
                  </div>
                  <button onClick={() => setEditing({ id: m.id, nom: m.nom, sujet: m.sujet, corps: m.corps })} className="w-8 h-8 rounded-full hover:bg-white flex items-center justify-center text-navy-400"><Pencil size={15} /></button>
                  <button onClick={() => supprimer(m.id)} className="w-8 h-8 rounded-full hover:bg-red-50 flex items-center justify-center text-navy-300 hover:text-red-500"><Trash2 size={15} /></button>
                </div>
              ))}
              <button onClick={nouveau} className="apple-btn-secondary w-full justify-center mt-2"><Plus size={15} /> Nouveau mail type</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------- Détail d'une demande ---------- */
function DemandeDetail({ demande, onChange, onRequestDelete }) {
  const [busy, setBusy] = useState(false);
  const [showReponse, setShowReponse] = useState(false);
  const { canSeeFinancials } = useAuth();
  const navigate = useNavigate();

  const faireDevis = async () => {
    setBusy(true);
    try {
      const { data } = await demandesApi.convertir(demande.id);
      if (data.enfant_id) navigate(`/devis/nouveau?enfant_id=${data.enfant_id}&demande_id=${demande.id}`);
      else { alert('Impossible de créer le devis (structure de la demande manquante ?).'); setBusy(false); }
    } catch (e) { alert(e.response?.data?.error || 'Erreur'); setBusy(false); }
  };
  const occupation = demande.analyse_planning?.occupation;
  const marge = canSeeFinancials ? demande.analyse_rentabilite?.marge : null;
  const verdict = canSeeFinancials ? demande.analyse_rentabilite?.verdict : null;
  const joursDem = occupation?.jours.filter(j => j.demande) || [];
  const tone = margeTone(marge);

  const act = async (fn, refresh = true) => {
    setBusy(true);
    try { await fn(); if (refresh) onChange(); }
    catch (err) { alert('Erreur : ' + (err.response?.data?.error || err.message)); }
    setBusy(false);
  };

  const Label = ({ icon: Icon, children }) => (
    <div className="text-[11px] uppercase tracking-wide text-gold-600 font-semibold flex items-center gap-1.5"><Icon size={12} /> {children}</div>
  );
  const InfoLine = ({ icon: Icon, children }) => (
    <div className="flex items-center gap-2 text-[13px] text-navy-500"><Icon size={13} className="text-navy-300 flex-shrink-0" />{children}</div>
  );
  const SectionTitle = ({ icon: Icon, children }) => (
    <h4 className="text-[13px] font-semibold text-navy-700 mb-3 flex items-center gap-2"><Icon size={14} className="text-gold-500" /> {children}</h4>
  );

  return (
    <div className="px-5 pb-5 pt-1 animate-expand">
      <div className="rounded-2xl bg-cream-100 border border-cream-300/60 p-5 space-y-6">

        {/* Identité */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <div className="space-y-1.5">
            <Label icon={Baby}>Enfant</Label>
            <div className="font-semibold text-navy-900">{demande.enfant_prenom || '?'} {demande.enfant_nom || ''}</div>
            <InfoLine icon={Clock}>Né(e) le {formatDate(demande.date_naissance)}</InfoLine>
            <InfoLine icon={CalendarDays}>Entrée : {formatDate(demande.date_entree_souhaitee)}</InfoLine>
          </div>
          <div className="space-y-1.5">
            <Label icon={Users}>Famille</Label>
            <div className="font-semibold text-navy-900">{demande.parent_nom || '—'}</div>
            <InfoLine icon={Mail}>{demande.parent_email || '—'}</InfoLine>
            <InfoLine icon={Phone}>{demande.parent_tel || '—'}</InfoLine>
          </div>
          <div className="space-y-1.5">
            <Label icon={CalendarDays}>Jours souhaités</Label>
            <div className="flex flex-wrap gap-1.5 pt-0.5">
              {demande.jours_souhaites?.length ? demande.jours_souhaites.map((j, i) => (
                <span key={i} className="px-2.5 py-1 bg-white border border-cream-300/70 rounded-full text-[12px] text-navy-600">
                  {JOURS_COURT[j.jour] || j.jour} · {j.debut}–{j.fin}
                </span>
              )) : <span className="text-[13px] text-navy-300">Non précisé</span>}
            </div>
          </div>
        </div>

        {/* Informations complémentaires du formulaire */}
        {(demande.genre || demande.commune || demande.revenu_fiscal_reference || demande.precisions_horaires || demande.commentaires || demande.creche_souhaitee || demande.rgpd_consent) && (
          <div className="rounded-2xl bg-white border border-cream-300/60 p-4 grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-2 text-[13px]">
            {demande.genre && <div><span className="text-navy-300">Genre : </span><span className="text-navy-700 font-medium">{demande.genre}</span></div>}
            {demande.creche_souhaitee && !demande.structure_nom && <div><span className="text-navy-300">Crèche souhaitée : </span><span className="text-navy-700 font-medium">{demande.creche_souhaitee}</span></div>}
            {demande.commune && <div><span className="text-navy-300">Commune : </span><span className="text-navy-700 font-medium">{demande.commune}</span></div>}
            {demande.revenu_fiscal_reference && <div><span className="text-navy-300">Revenu fiscal N-1 : </span><span className="text-navy-700 font-medium">{demande.revenu_fiscal_reference}</span></div>}
            {demande.precisions_horaires && <div className="md:col-span-2"><span className="text-navy-300">Précisions horaires : </span><span className="text-navy-700">{demande.precisions_horaires}</span></div>}
            {demande.commentaires && <div className="md:col-span-2"><span className="text-navy-300">Commentaires : </span><span className="text-navy-700">{demande.commentaires}</span></div>}
            {demande.rgpd_consent ? <div className="md:col-span-2 text-[12px] text-green-600 flex items-center gap-1.5"><Check size={13} /> Consentement RGPD accepté{demande.rgpd_consent_date ? ' le ' + formatDate(demande.rgpd_consent_date) : ''}</div> : null}
          </div>
        )}

        {/* Planning */}
        <div>
          <SectionTitle icon={CalendarDays}>Impact sur le planning</SectionTitle>
          {occupation ? (
            <div className="space-y-4">
              <div className="bg-white rounded-2xl border border-cream-300/60 p-4"><FillBar occupation={occupation} /></div>
              {joursDem.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5">
                  {joursDem.map(j => <DayChip key={j.jour} j={j} occupation={occupation} />)}
                </div>
              )}
              {!occupation.tous_rentrent && (
                <div className="flex items-center gap-2 text-[13px] text-red-500 bg-red-50 rounded-xl px-3 py-2">
                  <AlertTriangle size={14} />
                  {occupation.depassement_hebdo
                    ? `Remplissage hebdo dépassé (${occupation.taux_remplissage_apres}% > 100%)`
                    : `Plafond journalier dépassé : ${occupation.jours_satures.join(', ')}`}
                </div>
              )}
            </div>
          ) : <p className="text-[13px] text-navy-300">Structure non définie — analyse indisponible.</p>}
        </div>

        {/* Rentabilité */}
        {marge && (
          <div>
            <SectionTitle icon={TrendingUp}>Rentabilité</SectionTitle>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5">
              <div className="apple-tile bg-white">
                <div className="text-[20px] font-semibold text-navy-900 tracking-apple">{marge.ca_mensuel} €</div>
                <div className="text-[11px] text-navy-300 mt-0.5">CA mensuel</div>
              </div>
              <div className="apple-tile bg-white">
                <div className="text-[20px] font-semibold text-orange-500 tracking-apple">−{marge.cout_personnel_mensuel} €</div>
                <div className="text-[11px] text-navy-300 mt-0.5">Coût encadrement</div>
              </div>
              <div className="apple-tile !bg-navy-800 !border-navy-800">
                <div className={`text-[22px] font-semibold tracking-apple ${marge.marge_mensuelle >= 0 ? 'text-gold-300' : 'text-red-300'}`}>{marge.marge_mensuelle} €</div>
                <div className="text-[11px] text-navy-200 mt-0.5">Marge nette / mois</div>
              </div>
              <div className="apple-tile !bg-gold-50 !border-gold-200">
                <div className={`text-[20px] font-semibold tracking-apple ${tone.text}`}>{marge.marge_pct}%</div>
                <div className="text-[11px] text-navy-400 mt-0.5">{marge.marge_annuelle} €/an</div>
              </div>
            </div>
            <div className="mt-2 text-[11px] text-navy-300 text-center">
              {marge.heures_hebdo} h/sem · tarif {marge.tarif_horaire} €/h · {marge.heures_lissees} h lissées/mois
            </div>
          </div>
        )}

        {/* Verdict IA */}
        {verdict && (
          <div className="rounded-2xl bg-gradient-to-br from-gold-50 to-cream-100 border border-gold-200/70 p-4">
            <div className="text-[11px] uppercase tracking-wide text-gold-700 font-semibold mb-1.5 flex items-center gap-1.5"><Sparkles size={12} /> L'avis de Georges</div>
            <p className="text-[13.5px] leading-relaxed text-navy-700 whitespace-pre-line">{verdict}</p>
          </div>
        )}

        {/* Mail brut */}
        {demande.email_raw && (
          <details className="text-[13px]">
            <summary className="cursor-pointer text-navy-300 hover:text-navy-500 select-none">Mail d'origine</summary>
            <pre className="mt-2 p-3 bg-white border border-cream-300/60 rounded-xl text-[12px] whitespace-pre-wrap text-navy-500 max-h-56 overflow-auto">{demande.email_raw}</pre>
          </details>
        )}

        {/* Actions */}
        <div className="flex flex-wrap items-center gap-2 pt-1">
          <button disabled={busy} onClick={() => setShowReponse(true)} className="apple-btn-gold">
            <Mail size={15} /> Répondre
          </button>
          <button disabled={busy} onClick={() => act(() => demandesApi.setReservation(demande.id, demande.place_reservee ? 0 : 1))}
            className={demande.place_reservee ? 'apple-btn-primary' : 'apple-btn-secondary'}
            title="Le parent a-t-il bloqué la place ? (compte comme sûr dans le planning prévisionnel)">
            {demande.place_reservee ? '🔒 Place bloquée' : 'Bloquer la place'}
          </button>
          <button disabled={busy} onClick={faireDevis} className="apple-btn-primary">
            <FileText size={15} /> Faire un devis
          </button>
          {demande.statut !== 'convertie' && (
            <button disabled={busy} onClick={() => act(() => demandesApi.convertir(demande.id))} className="apple-btn-secondary">
              <ArrowRight size={15} /> Créer la préinscription
            </button>
          )}
          {demande.statut !== 'traitee' && (
            <button disabled={busy} onClick={() => act(() => demandesApi.updateStatut(demande.id, 'traitee'))} className="apple-btn-secondary">
              <CheckCircle2 size={15} /> Traitée
            </button>
          )}
          <button disabled={busy} onClick={() => act(() => demandesApi.reanalyser(demande.id))} className="apple-btn-ghost">
            <RefreshCw size={14} /> Recalculer
          </button>
          {demande.statut !== 'rejetee' && (
            <button disabled={busy} onClick={() => act(() => demandesApi.updateStatut(demande.id, 'rejetee'))} className="apple-btn-ghost">
              Rejeter
            </button>
          )}
          <button disabled={busy} onClick={() => onRequestDelete && onRequestDelete(demande)}
            title="Mettre à la corbeille (suppression définitive tracée RGPD)"
            className="apple-btn-ghost ml-auto text-navy-200 hover:text-red-500 hover:bg-red-50">
            <Trash2 size={15} />
          </button>
        </div>
      </div>
      {showReponse && (
        <ReponseModal demande={demande} onClose={() => setShowReponse(false)} onSent={onChange} />
      )}
    </div>
  );
}

/* ---------- Ligne de liste ---------- */
function DemandeRow({ d, expanded, onToggle, onChange, onRequestDelete }) {
  const { canSeeFinancials } = useAuth();
  const st = STATUT_MAP[d.statut] || STATUT_MAP.nouvelle;
  const occ = d.analyse_planning?.occupation;
  const marge = canSeeFinancials ? d.analyse_rentabilite?.marge : null;
  const tone = margeTone(marge);
  const sature = occ && !occ.tous_rentrent;
  const surnombre = !sature && occ?.jours_surnombre?.length > 0;

  return (
    <div className={expanded ? 'bg-cream-100/60' : ''}>
      <button onClick={onToggle} className="w-full text-left px-5 py-3.5 flex items-center gap-3.5 hover:bg-cream-100/70 transition-colors">
        {/* Avatar */}
        <div className="w-10 h-10 rounded-full flex items-center justify-center text-[13px] font-semibold flex-shrink-0"
          style={{ backgroundColor: hexA(d.structure_couleur, 0.16), color: d.structure_couleur || '#a87f3f' }}>
          {initials(d)}
        </div>
        {/* Texte */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-[15px] text-navy-900 truncate tracking-apple">
              {d.enfant_prenom || 'Enfant'} {d.enfant_nom || ''}
            </span>
            <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${st.pill}`}>{st.label}</span>
            {d.place_reservee ? <span className="text-[11px] px-2 py-0.5 rounded-full font-medium bg-gold-100 text-gold-800">🔒 Place bloquée</span> : null}
          </div>
          <div className="text-[12.5px] text-navy-300 truncate mt-0.5">
            {d.structure_nom || 'Structure ?'} · {relDate(d.created_at)}
          </div>
        </div>
        {/* Accessoires */}
        <div className="hidden sm:flex items-center gap-2 flex-shrink-0">
          {d.nb_reponses > 0 && (
            <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600 font-medium flex items-center gap-1"
              title={`${d.nb_reponses} réponse(s)${d.dernier_auteur ? ` · dernière par ${d.dernier_auteur}` : ''}${d.derniere_reponse ? ` le ${dateHeure(d.derniere_reponse)}` : ''}`}>
              <Mail size={11} /> Répondu{d.dernier_auteur ? ` · ${d.dernier_auteur}` : ''}{d.derniere_reponse ? ` · ${relDate(d.derniere_reponse)}` : ''}
            </span>
          )}
          {sature && (
            <span className="text-[11px] px-2 py-0.5 rounded-full bg-red-50 text-red-500 font-medium flex items-center gap-1">
              <AlertTriangle size={11} /> {occ.depassement_hebdo ? `${occ.taux_remplissage_apres}%` : `${occ.jours_satures.length}j complet`}
            </span>
          )}
          {surnombre && <span className="text-[11px] px-2 py-0.5 rounded-full bg-gold-100 text-gold-700 font-medium">surnombre</span>}
          {marge && <span className={`text-[12px] px-2.5 py-0.5 rounded-full font-semibold ${tone.pill}`}>{marge.marge_pct}%</span>}
        </div>
        <ChevronRight size={17} className={`text-navy-200 flex-shrink-0 transition-transform duration-200 ${expanded ? 'rotate-90' : ''}`} />
      </button>
      {expanded && <DemandeDetail demande={d} onChange={onChange} onRequestDelete={onRequestDelete} />}
    </div>
  );
}

/* ---------- Page ---------- */
/* ---------- Corbeille : suppression définitive sécurisée + trace RGPD ---------- */
function CorbeilleModal({ demande, onClose, onDeleted }) {
  const [motif, setMotif] = useState('');
  const [confirmTxt, setConfirmTxt] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const pret = motif.trim().length >= 3 && confirmTxt.trim().toUpperCase() === 'SUPPRIMER';

  const go = async () => {
    if (!pret) return;
    setBusy(true); setErr('');
    try { await demandesApi.delete(demande.id, motif.trim()); onDeleted(); }
    catch (e) { setErr(e.response?.data?.error || 'Erreur lors de la suppression'); setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
        <div className="flex items-center gap-2 mb-1">
          <Trash2 size={18} className="text-red-500" />
          <h3 className="font-serif text-lg text-navy-900">Supprimer définitivement</h3>
        </div>
        <p className="text-[13px] text-navy-500 mb-3">
          Demande de <strong>{demande.enfant_prenom || 'Enfant'} {demande.enfant_nom || ''}</strong>
          {demande.parent_email ? ` (${demande.parent_email})` : ''}. Cette action est <strong>irréversible</strong> et sera <strong>tracée au registre RGPD</strong> (droit à l'effacement).
        </p>
        {err && <div className="bg-rose-50 border border-rose-200 text-rose-700 text-sm rounded-lg px-3 py-2 mb-2">{err}</div>}
        <label className="text-xs text-navy-600 block mb-2">Motif de la suppression <span className="text-red-500">*</span>
          <textarea rows={2} className="form-input rounded-xl mt-1 text-sm" placeholder="ex : doublon, famille désistée, enfant déjà inscrit…" value={motif} onChange={e => setMotif(e.target.value)} />
        </label>
        <label className="text-xs text-navy-600 block mb-3">Tapez <strong>SUPPRIMER</strong> pour confirmer
          <input className="form-input rounded-xl mt-1 text-sm" value={confirmTxt} onChange={e => setConfirmTxt(e.target.value)} placeholder="SUPPRIMER" />
        </label>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-gray-300 text-sm text-gray-600">Annuler</button>
          <button onClick={go} disabled={!pret || busy} className={`px-4 py-2 rounded-lg text-sm text-white ${pret && !busy ? 'bg-red-500 hover:bg-red-600' : 'bg-gray-300 cursor-not-allowed'}`}>
            {busy ? 'Suppression…' : 'Supprimer définitivement'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function DemandesPage() {
  const { selectedStructure } = useApp();
  const [demandes, setDemandes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [polling, setPolling] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showModeles, setShowModeles] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [filtre, setFiltre] = useState('all');
  const [search, setSearch] = useState('');
  const [message, setMessage] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [purging, setPurging] = useState(false);
  const { user } = useAuth();
  const peutSupprimer = ['admin', 'direction'].includes(user?.role);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = selectedStructure ? { structure_id: selectedStructure } : {};
      const { data } = await demandesApi.getAll(params);
      setDemandes(data);
    } catch {
      setMessage({ type: 'error', text: 'Impossible de charger les demandes.' });
    }
    setLoading(false);
  }, [selectedStructure]);

  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => {
    const c = { all: 0, nouvelle: 0, traitee: 0, rejetee: 0 };
    demandes.forEach(d => { if (c[d.statut] != null) c[d.statut]++; if (d.statut !== 'rejetee') c.all++; });
    return c;
  }, [demandes]);

  const visibles = useMemo(() => {
    // « Toutes » = le pipe actif : on EXCLUT les demandes rejetées (visibles seulement via l'onglet « Rejetées »)
    let list = filtre === 'all' ? demandes.filter(d => d.statut !== 'rejetee') : demandes.filter(d => d.statut === filtre);
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter(d => [
        d.enfant_prenom, d.enfant_nom, d.parent_nom, d.parent_email, d.parent_tel, d.structure_nom,
      ].some(v => (v || '').toString().toLowerCase().includes(q)));
    }
    return list;
  }, [demandes, filtre, search]);

  const poll = async () => {
    setPolling(true); setMessage(null);
    try {
      const { data } = await demandesApi.poll();
      const s = data.stats || {};
      setMessage({ type: 'ok', text: `${s.traites || 0} nouvelle(s) demande(s) · ${s.deja || 0} déjà connue(s) · ${s.ignores || 0} ignorée(s).` });
      load();
    } catch (err) {
      setMessage({ type: 'error', text: 'Relevé impossible : ' + (err.response?.data?.error || 'IMAP non configuré (voir .env).') });
    }
    setPolling(false);
  };

  const purger = async () => {
    if (!window.confirm('Supprimer définitivement toutes les demandes dont l\'enfant est DÉJÀ INSCRIT (actif) ?\n\nChaque suppression sera tracée au registre RGPD. Action irréversible.')) return;
    setPurging(true); setMessage(null);
    try {
      const { data } = await demandesApi.purgerInscrits();
      setMessage({ type: 'ok', text: `${data.count} demande(s) d'enfants déjà inscrits supprimée(s) et tracée(s) au registre RGPD.` });
      load();
    } catch (err) {
      setMessage({ type: 'error', text: 'Purge impossible : ' + (err.response?.data?.error || 'erreur') });
    }
    setPurging(false);
  };

  return (
    <div className="-m-6 p-6 min-h-full bg-cream-200">
      {deleteTarget && (
        <CorbeilleModal
          demande={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onDeleted={() => { setDeleteTarget(null); setMessage({ type: 'ok', text: 'Demande supprimée définitivement et tracée au registre RGPD.' }); load(); }}
        />
      )}
      <div className="max-w-5xl mx-auto pb-16">
        {/* En-tête */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="font-serif text-[32px] leading-tight text-navy-900">Demandes entrantes</h1>
            <div className="w-12 h-0.5 bg-gold-400 rounded-full mt-2 mb-2.5" />
            <p className="text-navy-400 text-[14px]">Préinscriptions analysées automatiquement — planning &amp; rentabilité.</p>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setShowModeles(true)} className="apple-btn-ghost">
              <FileText size={15} /> Mails types
            </button>
            <button onClick={poll} disabled={polling} className="apple-btn-secondary">
              <RefreshCw size={15} className={polling ? 'animate-spin' : ''} /> Relever
            </button>
            {peutSupprimer && (
              <button onClick={purger} disabled={purging} className="apple-btn-ghost text-navy-400 hover:text-red-500 hover:bg-red-50"
                title="Supprimer définitivement les demandes des enfants déjà inscrits (tracé RGPD)">
                <Trash2 size={15} className={purging ? 'animate-pulse' : ''} /> Purger les déjà inscrits
              </button>
            )}
            <button onClick={() => setShowModal(true)} className="apple-btn-gold">
              <Plus size={16} /> Nouvelle demande
            </button>
          </div>
        </div>

        {/* Barre : filtre segmenté + recherche */}
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <div className="seg">
            {FILTRES.map(f => (
              <span key={f.key} onClick={() => setFiltre(f.key)}
                className={`seg-item ${filtre === f.key ? 'seg-item-active' : ''}`}>
                {f.label}
                {counts[f.key] > 0 && <span className="ml-1.5 text-[11px] opacity-60">{counts[f.key]}</span>}
              </span>
            ))}
          </div>
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-navy-300" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Rechercher un enfant, un parent, un e-mail…"
              className="form-input rounded-xl pl-9 pr-8 text-[13px] w-72"
            />
            {search && (
              <button onClick={() => setSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-navy-300 hover:text-navy-600" title="Effacer">
                <X size={14} />
              </button>
            )}
          </div>
        </div>

        {message && (
          <div className={`mb-4 px-4 py-2.5 rounded-xl text-[13px] flex items-center gap-2 ${message.type === 'error' ? 'bg-red-50 text-red-600' : 'bg-emerald-50 text-emerald-600'}`}>
            {message.type === 'error' ? <AlertTriangle size={14} /> : <CheckCircle2 size={14} />}
            {message.text}
          </div>
        )}

        {/* Liste */}
        {loading ? (
          <div className="text-center text-navy-300 py-20">Chargement…</div>
        ) : visibles.length === 0 ? (
          <div className="apple-card text-center py-16">
            <div className="w-14 h-14 rounded-2xl bg-cream-200 flex items-center justify-center mx-auto mb-4">
              <Inbox size={26} className="text-gold-500" />
            </div>
            <p className="text-navy-800 font-medium">{search.trim() ? 'Aucun résultat' : 'Aucune demande'}</p>
            <p className="text-[13px] text-navy-300 mt-1">{search.trim() ? `Aucune demande ne correspond à « ${search.trim()} ».` : 'Relevez les mails ou ajoutez une demande manuellement.'}</p>
          </div>
        ) : (
          <div className="apple-card divide-y divide-cream-300/50 overflow-hidden">
            {visibles.map(d => (
              <DemandeRow key={d.id} d={d} expanded={expanded === d.id}
                onToggle={() => setExpanded(expanded === d.id ? null : d.id)} onChange={load}
                onRequestDelete={peutSupprimer ? setDeleteTarget : null} />
            ))}
          </div>
        )}

        {showModal && <ManuelModal onClose={() => setShowModal(false)} onCreated={load} />}
        {showModeles && <ModelesManager onClose={() => setShowModeles(false)} />}
      </div>
    </div>
  );
}
